import React, { Fragment, useEffect, useState } from "react";
import {
    Box,
    Card,
    CardContent,
    Grid,
    IconButton,
    Typography,
    Menu,
    MenuItem,
    Tooltip,
    Chip,
    Button,
    FormControl,
    Select,
    TextField,
    Switch,
    FormControlLabel,
    useMediaQuery,
} from "@mui/material";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import { useLocation, useNavigate } from "react-router-dom";
import ProfileImageWithFallback from "../../../Components/ProfileImageWithFallback/ProfileImageWithFallback";
import moment from "moment";
import TopHeader from "../../../Components/TopHeader/TopHeader";
import { collection, getDocs, orderBy, query, where, onSnapshot, updateDoc, doc, deleteDoc } from "firebase/firestore";
import { DATABASE, FUNCTIONS } from "../../../Config/firebase";
import { httpsCallable } from "firebase/functions";
import EmailIcon from "@mui/icons-material/Email";
import LockResetIcon from "@mui/icons-material/LockReset";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import RefreshIcon from "@mui/icons-material/Refresh";
import TableContainer from "../../../Components/TableContainer/TableContainer";
import ReceiptIcon from '@mui/icons-material/Receipt';
import NewDepositModel from "../FundsManagement/FundsDeposit/NewDepositModel";
import NewWithdrowModel from "../FundsManagement/WithdrowFunds/NewWithdrowModel";
import { toast } from "react-toastify";
import PersonIcon from '@mui/icons-material/Person';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import Swal from "sweetalert2";
import Skeleton from "react-loading-skeleton";
import dayjs from "dayjs";
import DOCUMENT_ICON from "../../../Assets/Images/Common/documentation.png";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { formatWalletDisplay, buildWalletReference } from "../../../Utils/cryptoWalletUtils";
import { formatAppVersion, formatDevice, formatPlatform, formatReportedAt } from "../../../Utils/appVersionAdoptionUtils";
import { fetchKycProfile, saveUserBirthDate } from "../../../services/kycService";
import KycStatusChip from "../KycControlCenter/components/KycStatusChip";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import AcUnitIcon from "@mui/icons-material/AcUnit";
import AccountOperationsFreeze from "./AccountOperationsFreeze";
import CapitalRoutingWizard from "./CapitalRouting/CapitalRoutingWizard";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";

/** Sanitiza objeto para navigation state — remove funções (toDate, toMillis) que causam DataCloneError */
const sanitizeForNavigation = (obj) => {
    if (obj == null) return obj;
    if (typeof obj === "function") return undefined;
    if (Array.isArray(obj)) return obj.map(sanitizeForNavigation);
    if (obj && typeof obj === "object" && (obj.toDate || obj.toMillis)) {
        try {
            const date = obj.toDate ? obj.toDate() : new Date(obj.toMillis());
            return date?.toISOString ? date.toISOString() : date?.getTime?.() ?? null;
        } catch {
            return null;
        }
    }
    if (typeof obj === "object") {
        const out = {};
        for (const [k, v] of Object.entries(obj)) {
            if (typeof v === "function") continue;
            out[k] = sanitizeForNavigation(v);
        }
        return out;
    }
    return obj;
};

const ViewCustomer = () => {
    const [pageSize, setPageSize] = useState(50);
    const navigate = useNavigate();
    const location = useLocation();
    const [isLoading, setIsLoading] = useState(false);
    const [isBusy, setIsBusy] = useState(true);
    const [page, setPage] = useState(0);
    const [totalCount, setTotalCount] = useState(0);
    const [customerDetails, setCustomerDetails] = useState([]);
    const [anchorEl, setAnchorEl] = React.useState(null);
    const open = Boolean(anchorEl);
    const [transactionHistory, setTransactionHistory] = useState([]);
    const [lastMonthPerformances, setLastMonthPerformances] = useState([]);
    const handleClick = (event) => setAnchorEl(event.currentTarget)
    const handleClose = () => setAnchorEl(null);
    const [openDepositeModel, setOpenDepositeModel] = useState(false);
    const [openWithdrowModel, setOpenWithdrowModel] = useState(false);
    const [openCapitalRouting, setOpenCapitalRouting] = useState(false);
    const [isStatus, setIsStatus] = useState(null);
    const [isActivePageSize, setIsActivePageSize] = useState(null);
    const [totalPerformances, setTotalPerformances] = useState(0)
    const [cryptoWallets, setCryptoWallets] = useState([]);
    const [kycProfile, setKycProfile] = useState(null);
    const [birthDateEdit, setBirthDateEdit] = useState("");
    const [savingDob, setSavingDob] = useState(false);
    const isMobile = useMediaQuery("(max-width:960px)");
    const transactionHistoryDepositeCheck = transactionHistory?.filter((elem) =>
        elem?.user_id === customerDetails[0]?.doc_id &&
        elem?.request_type === 0 && elem?.request_status === 2
    );
    const totalDeposit = transactionHistoryDepositeCheck?.reduce((acc, elem) =>
        acc + (elem?.total_amount || 0),
        0
    );
    const transactionHistoryWithdrawCheck = transactionHistory?.filter((elem) =>
        elem?.user_id === customerDetails[0]?.doc_id &&
        elem?.request_type === 1 && elem?.request_status === 5
    );
    const totalWithdraw = transactionHistoryWithdrawCheck?.reduce((acc, elem) =>
        acc + (elem?.total_amount || 0),
        0
    );
    const columns = [
        {
            field: "rowid",
            headerName: "Sr.No",
            minWidth: 100,
            filterable: false,
            editable: false,
            sortable: false,
            disableColumnMenu: true,
        },
        {
            field: "request_type",
            headerName: "Type",
            minWidth: 200,
            filterable: true,
            sortable: true,
            renderCell: (params) => (
                <Tooltip title={params?.row?.request_type || "--"}>
                    <span>{params?.row?.request_type === 0 ? "Deposit" : "withdraw" || "--"}</span>
                </Tooltip>
            ),
        },
        {
            field: "total_amount",
            headerName: "Amount",
            minWidth: 200,
            filterable: false,
            editable: false,
            sortable: false,
            disableColumnMenu: true,
            renderCell: (params) => {
                const balance = params?.row?.total_amount || 0;
                const formattedBalance = new Intl.NumberFormat('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                }).format(balance);

                return (
                    <Tooltip title={formattedBalance}>
                        <span>{`$${formattedBalance}`}</span>
                    </Tooltip>
                );
            },
        },
        {
            field: "txn_id",
            headerName: "Transaction ID",
            minWidth: 200,
            filterable: false,
            editable: false,
            sortable: false,
            disableColumnMenu: true,
            renderCell: (params) => (
                <Tooltip title={params?.row?.txn_id}>
                    <span>{params?.row?.txn_id}</span>
                </Tooltip>

            ),
        },
        {
            field: "payment_receipt",
            headerName: "Payment Receipt",
            minWidth: 200,
            filterable: false,
            editable: false,
            sortable: false,
            disableColumnMenu: true,
            renderCell: (params) => {
                const url = params?.row?.payment_receipt?.replace(/fiddux-5a1f1\.appspot\.com/g, "fidduxai-rowy.appspot.com") || params?.row?.payment_receipt;
                return (
                    <Tooltip title={'Download Receipt'}>
                        <IconButton onClick={() => window.open(url, '_blank')}>
                            <ReceiptIcon />
                        </IconButton>
                    </Tooltip>
                );
            },
        },
        {
            field: "date",
            headerName: "Registered Date",
            minWidth: 250,
            renderCell: (params) => {
                const { date } = params?.row;

                const seconds = date?.seconds;
                const nanoseconds = date?.nanoseconds;
                const milliseconds = seconds * 1000 + Math.floor(nanoseconds / 1000000);
                const dateT = moment(milliseconds);
                return (
                    <div>
                        {dateT.format('MMMM Do YYYY, h:mm:ss A')}
                    </div>
                );
            },
        },
        {
            field: "request_status",
            headerName: "Status",
            minWidth: 150,
            filterable: false,
            editable: false,
            sortable: false,
            disableColumnMenu: true,
            renderCell: (params) => {
                let status = params?.row?.request_status;
                if (status === 0) {
                    return <Chip label="Pending" sx={{ color: '#fff', backgroundColor: "#FFC107", cursor: 'pointer' }} />;
                } else if (status === 1) {
                    return <Chip label="Waiting" sx={{ color: '#fff', backgroundColor: "#FF9800", cursor: 'pointer' }} />;
                } else if (status === 2) {
                    return <Chip label="Approved" sx={{ color: '#fff', backgroundColor: "#4CAF50", cursor: 'pointer' }} />;
                } else if (status === 3) {
                    return <Chip label="Cancelled" sx={{ color: '#fff', backgroundColor: "#F44336", cursor: 'pointer' }} />;
                } else if (status === 4) {
                    return <Chip label="Rejected" sx={{ color: '#ff', backgroundColor: "#D32F2F", cursor: 'pointer' }} />;
                } else if (status === 5) {
                    return <Chip label="Paid" sx={{
                        background: "#4caf50",
                        color: '#fff',
                        "&:hover": {
                            background: "#388e3c",
                        },
                    }} />
                }
            },
        },
    ];
    useEffect(() => {
        const customData = location?.state?.customData;
        const checkCustomData = location?.state?.typeCheck;
        const pageSizeFromState = location?.state?.page || 0;
        if (customData) {
            setIsActivePageSize(pageSizeFromState)
            const customerID = checkCustomData === "Withdraw" ? customData?.user_id : customData?.doc_id;
            getCustomerDetails(customerID);
        }
    }, [location?.state?.customData]);
    const getCustomerDetails = async (ID) => {
        setIsLoading(true)
        try {
            const transactionRef = collection(DATABASE, "users");
            const q = query(transactionRef, where("doc_id", "==", ID));
            const querySnapshot = await getDocs(q);
            const data = querySnapshot?.docs.map((doc) => doc?.data())
            setCustomerDetails(data)
            setBirthDateEdit(data?.[0]?.birth_date || "");
            if (data?.[0]?.doc_id) {
                loadCryptoWallets(data[0].doc_id);
                fetchKycProfile(data[0].doc_id)
                    .then((p) => {
                        setKycProfile(p);
                        // Prefer users.birth_date; fall back to KYC mirror if missing
                        if (!data[0]?.birth_date && p?.birth_date) {
                            setBirthDateEdit(p.birth_date);
                        }
                    })
                    .catch(() => setKycProfile(null));
            }
            return data;
        } catch (error) {
            console.error("Error fetching customer details:", error);
            return [];
        } finally {
            setIsLoading(false)
        }
    };
    const loadCryptoWallets = async (userId) => {
        try {
            const walletsRef = collection(DATABASE, "users", userId, "crypto_wallets");
            const snap = await getDocs(walletsRef);
            const list = snap.docs
                .map((d) => ({ wallet_doc_id: d.id, ...d.data() }))
                .filter((w) => w.is_active !== false)
                .sort((a, b) => (a.id || "").localeCompare(b.id || ""));
            setCryptoWallets(list);
        } catch (error) {
            console.error("Error loading crypto wallets:", error);
            setCryptoWallets([]);
        }
    };
    useEffect(() => {
        const customData = location?.state?.customData;
        if (customData) {
            const customerID = customData?.doc_id;
            getTransactionHistory(page, pageSize, isStatus, customerID);
        }
    }, [page, pageSize, isStatus, location?.state?.customData]);
    const getTransactionHistory = async (currentPage = 0, pageSize = 50, statusFilter = null, customerID) => {
        setIsBusy(true);
        try {
            const queryRef = collection(DATABASE, "transection_requests");
            let firestoreQuery = query(queryRef);
            if (customerID) {
                firestoreQuery = query(firestoreQuery, where("user_id", "==", customerID));
            }
            if (statusFilter !== null) {
                firestoreQuery = query(firestoreQuery, where("request_type", "==", statusFilter));
            }
            firestoreQuery = query(firestoreQuery, orderBy("date", "desc"));
            const unsubscribe = onSnapshot(firestoreQuery, (querySnapshot) => {
                const allData = querySnapshot?.docs?.map((doc, index) => ({
                    rowid: index + 1,
                    id: doc?.id,
                    ...doc.data(),
                }));
                const lastMonth = dayjs().subtract(1, 'month');

                const lastMonthPerformances = allData?.filter(item => {
                    const itemDate = dayjs(item?.date?.toDate());
                    return itemDate.month() === lastMonth?.month() && item?.request_type === 2;
                });
                const totalPerformance = allData?.reduce((acc, item) => {
                    return acc + (item?.profit_loss || 0);
                }, 0);
                setTotalPerformances(totalPerformance)
                setLastMonthPerformances(lastMonthPerformances)
                const filteredData = allData?.filter(item => item?.request_type !== 2);
                setTotalCount(filteredData.length);
                setTransactionHistory(filteredData.slice(currentPage * pageSize, (currentPage + 1) * pageSize));
            });
            return unsubscribe;

        } catch (error) {
            console.error("Error fetching transaction history:", error);
        } finally {
            setIsBusy(false);
        }
    };
    const handleSubmitedDeposit = () => {
        setOpenDepositeModel(false);
        const customerID = location?.state?.customData?.doc_id;
        getTransactionHistory(page, pageSize, isStatus, customerID);
        getCustomerDetails(customerID);
    }
    const handleSubmitedWithdraw = () => {
        setOpenDepositeModel(false);
        const customerID = location?.state?.customData?.doc_id;
        getTransactionHistory(page, pageSize, isStatus, customerID);
        getCustomerDetails(customerID);
    }
    const handlePageChange = (newPage) => {
        setPage(newPage);
        getTransactionHistory(newPage, pageSize, isStatus);
    };
    const handlePageSizeChange = (newPageSize) => {
        setPageSize(newPageSize);
        setPage(0);
        getTransactionHistory(0, newPageSize, isStatus);
    };
    const handleUpdateStatusDepositWithdraw = (id) => {
        setIsStatus(id === null ? null : id);
        setPage(0);
    }
    const handleUpdateBlockUnBlockStatus = async (docId, newStatus) => {
        const result = await Swal.fire({
            title: "Are you sure?",
            text: `You want to ${newStatus === 1 ? "UnBlock" : newStatus === 2 ? "Block" : "Reject"} this customer`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#46adf2",
            cancelButtonColor: "#5b5d5d",
            confirmButtonText: "Yes",
            cancelButtonText: "Cancel",
        });

        if (result.isConfirmed) {
            try {
                await updateDoc(doc(DATABASE, "users", docId), { status: newStatus });
                toast.success(newStatus === 1 ? "UnBlock!" : "Blocked!", `Customer has been ${newStatus === 1 ? "accepted" : "Blocked"}.`, "success");
                const customData = location?.state?.customData;
                getCustomerDetails(customData?.doc_id);
            } catch (error) {
                console.error("Error updating customer status:", error);
                toast.success("Error!", "Something went wrong. Try again!", "error");
            }
        }
    };

    const handleUpdatePayoutMode = async (docId, newMode) => {
        if (!docId) return;
        const isFiat = newMode === "fiat_manual";
        const result = await Swal.fire({
            title: "Change withdrawal payout?",
            text: isFiat
                ? "This client will be paid in FIAT manually by Fiddux. Auto-Pay will NEVER process their withdrawals and they can request a withdrawal without a registered crypto wallet."
                : "This client will use the normal crypto withdrawal flow (wallet required, Auto-Pay eligible).",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#46adf2",
            cancelButtonColor: "#5b5d5d",
            confirmButtonText: "Yes, save",
            cancelButtonText: "Cancel",
        });
        if (!result.isConfirmed) return;
        try {
            await updateDoc(doc(DATABASE, "users", docId), { payout_mode: newMode });
            toast.success("Withdrawal payout updated.");
            getCustomerDetails(docId);
        } catch (error) {
            console.error("Error updating payout mode:", error);
            toast.error("Failed to update withdrawal payout.");
        }
    };

    const handleResetYieldToggleCount = async (docId, userEmail, userName) => {
        if (!docId) return;
        const result = await Swal.fire({
            title: "Reset Yield Toggle Count?",
            text: `This will reset the monthly toggle count for ${userName || userEmail || "this user"}. They will be able to toggle Yield products again.`,
            icon: "question",
            showCancelButton: true,
            confirmButtonColor: "#46adf2",
            cancelButtonColor: "#5b5d5d",
            confirmButtonText: "Yes, Reset",
            cancelButtonText: "Cancel",
        });

        if (!result.isConfirmed) return;
        try {
            await updateDoc(doc(DATABASE, "users", docId), {
                yield_toggles_this_month: 0,
            });
            toast.success(`Yield toggle count has been reset for ${userName || userEmail || "this user"}.`);
            getCustomerDetails(docId);
        } catch (error) {
            console.error("Error resetting toggle count:", error);
            toast.error("Failed to reset toggle count.");
        }
    };

    const handleSaveBirthDate = async () => {
        const docId = customerDetails[0]?.doc_id;
        if (!docId) return;
        const trimmed = (birthDateEdit || "").trim();
        if (trimmed && !/^\d{4}-\d{2}-\d{2}$/.test(trimmed) && !/^\d{1,2}[/.-]\d{1,2}[/.-]\d{2,4}$/.test(trimmed)) {
            toast.error("Use YYYY-MM-DD (or DD/MM/YYYY).");
            return;
        }
        setSavingDob(true);
        try {
            const saved = await saveUserBirthDate(docId, trimmed);
            if (!saved && trimmed) {
                toast.error("Could not parse date of birth.");
                return;
            }
            toast.success(saved ? `Date of birth saved: ${saved}` : "Date of birth cleared.");
            setBirthDateEdit(saved || "");
            getCustomerDetails(docId);
        } catch (error) {
            console.error("Error saving birth date:", error);
            toast.error(error.message || "Failed to save date of birth.");
        } finally {
            setSavingDob(false);
        }
    };

    const handleToggleDoNotContact = async (checked) => {
        const docId = customerDetails[0]?.doc_id;
        if (!docId) return;
        try {
            await updateDoc(doc(DATABASE, "users", docId), { do_not_contact: !!checked });
            toast.success(checked ? "Marked as Do not contact" : "Do not contact removed");
            getCustomerDetails(docId);
        } catch (error) {
            console.error("Error updating do_not_contact:", error);
            toast.error("Failed to update Do not contact");
        }
    };

    const handleResendVerificationEmail = async (userId, userEmail) => {
        try {
            const resendVerification = httpsCallable(FUNCTIONS, "resendVerificationEmail");
            
            Swal.fire({
                title: 'Resending Verification Email',
                text: 'Please wait...',
                allowOutsideClick: false,
                didOpen: () => {
                    Swal.showLoading();
                }
            });

            const result = await resendVerification({ userId, email: userEmail });
            
            if (result.data.success) {
                Swal.fire({
                    icon: 'success',
                    title: 'Email Sent',
                    text: `Verification email sent to ${result.data.email}`,
                });
            } else {
                Swal.fire({
                    icon: 'info',
                    title: 'Email Already Verified',
                    text: result.data.message || 'This email is already verified.',
                });
            }
        } catch (error) {
            console.error('Error resending verification email:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error.message || 'Failed to resend verification email. Please try again.',
            });
        }
    };

    const handleSendPasswordResetEmail = async (userId, userEmail) => {
        try {
            const sendPasswordReset = httpsCallable(FUNCTIONS, "sendPasswordResetEmailAdmin");
            
            Swal.fire({
                title: 'Sending Password Reset Email',
                text: 'Please wait...',
                allowOutsideClick: false,
                didOpen: () => {
                    Swal.showLoading();
                }
            });

            const result = await sendPasswordReset({ userId, email: userEmail });
            
            if (result.data.success) {
                Swal.fire({
                    icon: 'success',
                    title: 'Email Sent',
                    text: `Password reset email sent to ${result.data.email}`,
                });
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: result.data.message || 'Failed to send password reset email.',
                });
            }
        } catch (error) {
            console.error('Error sending password reset email:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error.message || 'Failed to send password reset email. Please try again.',
            });
        }
    };

    const handleDeleteUser = async (docId, userEmail, userName) => {
        // Step 1: Password prompt
        const { value: password } = await Swal.fire({
            title: "Admin Password Required",
            text: "Please enter the admin password to delete this user:",
            input: "password",
            inputPlaceholder: "Enter password",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#5b5d5d",
            confirmButtonText: "Continue",
            cancelButtonText: "Cancel",
            inputValidator: (value) => {
                if (!value) {
                    return "Password is required";
                }
            },
        });

        if (!password) {
            return;
        }

        // Step 1.5: Validate password via Cloud Function
        try {
            const validatePassword = httpsCallable(FUNCTIONS, "validateAdminPassword");
            
            const result = await validatePassword({
                password: password.trim(),
                action: "delete-user"
            });

            if (!result.data.valid) {
                Swal.fire({
                    icon: "error",
                    title: "Access Denied",
                    text: "Incorrect password. Please try again.",
                });
                return;
            }
        } catch (error) {
            console.error("Error validating password:", error);
            Swal.fire({
                icon: "error",
                title: "Error",
                text: "Error validating password. Please try again.",
            });
            return;
        }

        setIsLoading(true);

        try {
            // Step 2: Final confirmation
            const result = await Swal.fire({
                title: "⚠️ Delete User?",
                html: `Are you sure you want to permanently delete:<br/><br/><strong>User Name:</strong> ${userName || "N/A"}<br/><strong>Email:</strong> ${userEmail || "N/A"}<br/><br/>This action cannot be undone!`,
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#d33",
                cancelButtonColor: "#5b5d5d",
                confirmButtonText: "Yes, Delete",
                cancelButtonText: "Cancel",
            });

            if (result.isConfirmed) {
                // Step 3: Delete user
                const userDocRef = doc(DATABASE, "users", docId);
                await deleteDoc(userDocRef);
                console.log("✅ User deleted successfully");

                await Swal.fire({
                    title: "Deleted!",
                    text: "User has been permanently deleted.",
                    icon: "success",
                    confirmButtonColor: "#46adf2",
                });

                toast.success("Deleted!", "User has been permanently deleted.", "success");

                // Navigate back to customer list
                navigate("/admin/customermanagement");
            }
        } catch (error) {
            console.error("❌ Error deleting user:", error);
            
            let errorMessage = "Failed to delete user. Please try again.";
            
            if (error.code === "permission-denied") {
                errorMessage = "Permission denied. You may not have permission to delete users.";
            } else if (error.code === "not-found") {
                errorMessage = "User document not found. It may have already been deleted.";
            } else if (error.message) {
                errorMessage = error.message;
            }
            
            await Swal.fire({
                title: "Error!",
                html: `<strong>Failed to delete user</strong><br/><br/>${errorMessage}`,
                icon: "error",
                confirmButtonColor: "#46adf2",
            });
            toast.error("Error!", errorMessage, "error");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Fragment>
            <TopHeader title="View Customer" onClick={() => navigate(-1)} />
            <Box
                sx={{
                    padding: "100px 15px 40px 15px",
                    maxWidth: { xs: "100%" },
                    margin: "0 auto",
                    width: "100%",
                }}
            >
                <Grid container spacing={4}>
                    <Grid item xs={12} sm={5} md={5} lg={3}>
                        <Card
                            sx={{
                                background: "linear-gradient(148.97deg, #0C2C50 0%, #450F5B 100%)",
                                position: "relative",
                            }}
                        >
                            <CardContent>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={12} md={12}>
                                        {!isLoading && customerDetails[0] && (
                                            <Box sx={{ mb: 1.5 }}>
                                                <Typography
                                                    sx={{
                                                        color: "#ffffff",
                                                        fontSize: { xs: "20px", sm: "22px" },
                                                        fontWeight: 600,
                                                        fontFamily: "Poppins",
                                                        textTransform: "capitalize",
                                                        lineHeight: 1.3,
                                                    }}
                                                >
                                                    {customerDetails[0]?.full_name || "Unknown"}
                                                </Typography>
                                                <Typography
                                                    sx={{
                                                        color: "rgba(255,255,255,0.78)",
                                                        fontSize: "14px",
                                                        fontFamily: "Poppins",
                                                        mt: 0.5,
                                                        wordBreak: "break-word",
                                                    }}
                                                >
                                                    {customerDetails[0]?.email || "—"}
                                                </Typography>
                                            </Box>
                                        )}
                                        {isLoading ? <Skeleton height={200} /> : <Box
                                            sx={{
                                                height: "200px",
                                                position: "relative",
                                            }}
                                        >
                                            <ProfileImageWithFallback
                                                src={customerDetails[0]?.profile_image || customerDetails[0]?.profile_img}
                                                alt={customerDetails[0]?.full_name ? `${customerDetails[0].full_name} profile` : "Customer profile"}
                                                width="100%"
                                                height="100%"
                                                objectFit="contain"
                                                style={{ objectPosition: "top" }}
                                            />
                                            {/* Account status — 1 chip only. Frozen hides Active. */}
                                            <Box
                                                sx={{
                                                    position: "absolute",
                                                    left: 8,
                                                    bottom: 8,
                                                    display: "flex",
                                                    flexWrap: "wrap",
                                                    gap: 0.75,
                                                    maxWidth: "calc(100% - 16px)",
                                                }}
                                            >
                                                {(() => {
                                                    const isFrozen = customerDetails[0]?.account_operations_frozen === true;
                                                    if (isFrozen) {
                                                        return (
                                                            <Chip
                                                                icon={<AcUnitIcon sx={{ color: "#fff !important", fontSize: "14px !important" }} />}
                                                                label="Frozen"
                                                                size="small"
                                                                sx={{ color: "#fff", backgroundColor: "#0288d1", fontWeight: 600, fontFamily: "Poppins" }}
                                                            />
                                                        );
                                                    }
                                                    const st = customerDetails[0]?.status;
                                                    if (st === 0) {
                                                        return (
                                                            <Chip
                                                                label="Pending"
                                                                size="small"
                                                                sx={{ color: "#fff", backgroundColor: "#FFC107", fontWeight: 600, fontFamily: "Poppins" }}
                                                            />
                                                        );
                                                    }
                                                    if (st === 2 || st === 3) {
                                                        return (
                                                            <Chip
                                                                label="Blocked"
                                                                size="small"
                                                                sx={{ color: "#fff", backgroundColor: "#F44336", fontWeight: 600, fontFamily: "Poppins" }}
                                                            />
                                                        );
                                                    }
                                                    if (st === 1) {
                                                        return (
                                                            <Chip
                                                                label="Active"
                                                                size="small"
                                                                sx={{ color: "#fff", backgroundColor: "#4CAF50", fontWeight: 600, fontFamily: "Poppins" }}
                                                            />
                                                        );
                                                    }
                                                    return (
                                                        <Chip
                                                            label="Unknown"
                                                            size="small"
                                                            sx={{ color: "#fff", backgroundColor: "#9e9e9e", fontWeight: 600, fontFamily: "Poppins" }}
                                                        />
                                                    );
                                                })()}
                                            </Box>
                                        </Box>}
                                    </Grid>
                                    <Grid item xs={12} sm={12} md={12}>
                                        <Grid container spacing={1}>
                                            <Grid item xs={12} sm={12} md={12}>
                                                <Card
                                                    sx={{
                                                        bgcolor: "background.paper", // Usa o background do tema (dark mode)
                                                    }}
                                                >
                                                    <CardContent>
                                                        <Typography
                                                            sx={{
                                                                fontSize: "18px",
                                                                fontWeight: "600",
                                                                color: "text.primary",
                                                                textAlign: "left",
                                                                width: "100%",
                                                                paddingBottom: "10px",
                                                                fontFamily: "Poppins",
                                                            }}
                                                        >
                                                            Basic information
                                                        </Typography>
                                                        <Grid container spacing={3}>
                                                            <Grid item xs={12} md={12}>
                                                                <Typography
                                                                    sx={{
                                                                        fontSize: "16px",
                                                                        fontWeight: "400",
                                                                        color: "text.primary",
                                                                        textAlign: "left",
                                                                        width: "100%",
                                                                        fontFamily: "Poppins",
                                                                    }}
                                                                >
                                                                    Full Name
                                                                </Typography>
                                                                {isLoading ? <Skeleton height={20} /> : <Typography
                                                                    sx={{
                                                                        fontSize: "14px",
                                                                        fontWeight: "400",
                                                                        color: "text.primary",
                                                                        textAlign: "left",
                                                                        width: "100%",
                                                                        paddingTop: "4px",
                                                                        textTransform: "capitalize",
                                                                        fontFamily: "Poppins",
                                                                    }}
                                                                >
                                                                    {customerDetails[0]?.full_name
                                                                        ? customerDetails[0]?.full_name
                                                                        : "N/A"}
                                                                </Typography>}
                                                            </Grid>
                                                            <Grid item xs={12} md={12}>
                                                                <Typography
                                                                    sx={{
                                                                        fontSize: "16px",
                                                                        fontWeight: "400",
                                                                        color: "text.primary",
                                                                        textAlign: "left",
                                                                        width: "100%",
                                                                        fontFamily: "Poppins",
                                                                    }}
                                                                >
                                                                    Email
                                                                </Typography>
                                                                {isLoading ? <Skeleton height={20} /> : <Typography
                                                                    sx={{
                                                                        fontSize: "14px",
                                                                        fontWeight: "400",
                                                                        color: "text.primary",
                                                                        textAlign: "left",
                                                                        width: "100%",
                                                                        paddingTop: "4px",
                                                                        fontFamily: "Poppins",
                                                                    }}
                                                                >
                                                                    {customerDetails[0]?.email
                                                                        ? customerDetails[0]?.email
                                                                        : "N/A"}
                                                                </Typography>}
                                                            </Grid>
                                                            <Grid item xs={12} md={12}>
                                                                <Typography
                                                                    sx={{
                                                                        fontSize: "16px",
                                                                        fontWeight: "400",
                                                                        color: "text.primary",
                                                                        textAlign: "left",
                                                                        width: "100%",
                                                                        fontFamily: "Poppins",
                                                                    }}
                                                                >
                                                                    User ID
                                                                </Typography>
                                                                {isLoading ? <Skeleton height={20} /> : (
                                                                    <Box sx={{ display: "flex", alignItems: "center", gap: "4px", paddingTop: "4px" }}>
                                                                        <Typography
                                                                            sx={{
                                                                                fontSize: "14px",
                                                                                fontWeight: "400",
                                                                                color: "text.primary",
                                                                                textAlign: "left",
                                                                                fontFamily: "monospace",
                                                                                wordBreak: "break-all",
                                                                            }}
                                                                        >
                                                                            {customerDetails[0]?.doc_id || "N/A"}
                                                                        </Typography>
                                                                        {customerDetails[0]?.doc_id && (
                                                                            <Tooltip title="Copy user ID">
                                                                                <IconButton
                                                                                    size="small"
                                                                                    onClick={() => { navigator.clipboard.writeText(customerDetails[0].doc_id); toast.info("User ID copied"); }}
                                                                                >
                                                                                    <ContentCopyIcon sx={{ fontSize: 14 }} />
                                                                                </IconButton>
                                                                            </Tooltip>
                                                                        )}
                                                                    </Box>
                                                                )}
                                                            </Grid>
                                                            <Grid item xs={12} md={12}>
                                                                <Typography
                                                                    sx={{
                                                                        fontSize: "16px",
                                                                        fontWeight: "400",
                                                                        color: "text.primary",
                                                                        textAlign: "left",
                                                                        width: "100%",
                                                                        fontFamily: "Poppins",
                                                                    }}
                                                                >
                                                                    Wallet Reference
                                                                </Typography>
                                                                {isLoading ? <Skeleton height={20} /> : (
                                                                    <Box sx={{ display: "flex", alignItems: "center", gap: "4px", paddingTop: "4px" }}>
                                                                        <Typography
                                                                            sx={{
                                                                                fontSize: "14px",
                                                                                fontWeight: "400",
                                                                                color: "text.primary",
                                                                                textAlign: "left",
                                                                                fontFamily: "monospace",
                                                                            }}
                                                                        >
                                                                            {customerDetails[0]?.doc_id
                                                                                ? buildWalletReference(customerDetails[0]?.full_name, customerDetails[0]?.doc_id)
                                                                                : "N/A"}
                                                                        </Typography>
                                                                        {customerDetails[0]?.doc_id && (
                                                                            <Tooltip title="Copy wallet reference (Bybit label)">
                                                                                <IconButton
                                                                                    size="small"
                                                                                    onClick={() => {
                                                                                        navigator.clipboard.writeText(
                                                                                            buildWalletReference(customerDetails[0]?.full_name, customerDetails[0]?.doc_id)
                                                                                        );
                                                                                        toast.info("Wallet reference copied");
                                                                                    }}
                                                                                >
                                                                                    <ContentCopyIcon sx={{ fontSize: 14 }} />
                                                                                </IconButton>
                                                                            </Tooltip>
                                                                        )}
                                                                    </Box>
                                                                )}
                                                            </Grid>
                                                            <Grid item xs={12} md={12}>
                                                                <Typography
                                                                    sx={{
                                                                        fontSize: "16px",
                                                                        fontWeight: "400",
                                                                        color: "text.primary",
                                                                        textAlign: "left",
                                                                        width: "100%",
                                                                        fontFamily: "Poppins",
                                                                    }}
                                                                >
                                                                    Mobile Number
                                                                </Typography>
                                                                {isLoading ? <Skeleton height={20} /> : <Typography
                                                                    sx={{
                                                                        fontSize: "14px",
                                                                        fontWeight: "400",
                                                                        color: "text.primary",
                                                                        textAlign: "left",
                                                                        width: "100%",
                                                                        paddingTop: "4px",
                                                                        textTransform: "capitalize",
                                                                        fontFamily: "Poppins",
                                                                    }}
                                                                >
                                                                    {customerDetails[0]?.phone
                                                                        ? `${customerDetails[0]?.phone?.split(",")[1]} - ${customerDetails[0]?.phone?.split(",")[2]}`
                                                                        : "N/A"}
                                                                </Typography>}
                                                            </Grid>
                                                            <Grid item xs={12} md={12}>
                                                                <Typography
                                                                    sx={{
                                                                        fontSize: "16px",
                                                                        fontWeight: "400",
                                                                        color: "text.primary",
                                                                        textAlign: "left",
                                                                        width: "100%",
                                                                        fontFamily: "Poppins",
                                                                    }}
                                                                >
                                                                    User Type
                                                                </Typography>
                                                                {isLoading ? <Skeleton height={20} /> : <Typography
                                                                    sx={{
                                                                        fontSize: "14px",
                                                                        fontWeight: "400",
                                                                        color: "text.primary",
                                                                        textAlign: "left",
                                                                        width: "100%",
                                                                        paddingTop: "4px",
                                                                        textTransform: "capitalize",
                                                                        fontFamily: "Poppins",
                                                                    }}
                                                                >
                                                                    {customerDetails[0]?.user_type === 0 
                                                                        ? `Individual Customer`
                                                                        : "Corporate Customer"}
                                                                </Typography>}
                                                            </Grid>
                                                            <Grid item xs={12} md={12}>
                                                                <Typography
                                                                    sx={{
                                                                        fontSize: "16px",
                                                                        fontWeight: "400",
                                                                        color: "text.primary",
                                                                        textAlign: "left",
                                                                        width: "100%",
                                                                        fontFamily: "Poppins",
                                                                    }}
                                                                >
                                                                    App Version
                                                                </Typography>
                                                                {isLoading ? <Skeleton height={20} /> : (
                                                                    customerDetails[0]?.app_build_number != null ? (
                                                                        <Typography
                                                                            sx={{
                                                                                fontSize: "14px",
                                                                                fontWeight: "400",
                                                                                color: "text.primary",
                                                                                textAlign: "left",
                                                                                width: "100%",
                                                                                paddingTop: "4px",
                                                                                fontFamily: "Poppins",
                                                                            }}
                                                                        >
                                                                            {formatAppVersion(customerDetails[0])}
                                                                            {` · Build ${customerDetails[0]?.app_build_number}`}
                                                                            {` · ${formatPlatform(customerDetails[0])}`}
                                                                            {customerDetails[0]?.app_device_model
                                                                              ? ` · ${formatDevice(customerDetails[0])}`
                                                                              : ""}
                                                                            {` · Last reported ${formatReportedAt(customerDetails[0]?.app_version_reported_at)}`}
                                                                        </Typography>
                                                                    ) : (
                                                                        <Typography
                                                                            sx={{
                                                                                fontSize: "14px",
                                                                                fontWeight: "400",
                                                                                color: "#9e9e9e",
                                                                                textAlign: "left",
                                                                                width: "100%",
                                                                                paddingTop: "4px",
                                                                                fontStyle: "italic",
                                                                                fontFamily: "Poppins",
                                                                            }}
                                                                        >
                                                                            Not reported yet
                                                                        </Typography>
                                                                    )
                                                                )}
                                                            </Grid>
                                                            <Grid item xs={12} md={12}>
                                                                <Typography
                                                                    sx={{
                                                                        fontSize: "16px",
                                                                        fontWeight: "400",
                                                                        color: "text.primary",
                                                                        textAlign: "left",
                                                                        width: "100%",
                                                                        fontFamily: "Poppins",
                                                                    }}
                                                                >
                                                                    Document Number
                                                                </Typography>
                                                                {isLoading ? <Skeleton height={20} /> : <Typography
                                                                    sx={{
                                                                        fontSize: "14px",
                                                                        fontWeight: "400",
                                                                        color: "text.primary",
                                                                        textAlign: "left",
                                                                        width: "100%",
                                                                        paddingTop: "4px",
                                                                        fontFamily: "Poppins",
                                                                    }}
                                                                >
                                                                    {customerDetails[0]?.document_number
                                                                        ? customerDetails[0]?.document_number
                                                                        : "N/A"}
                                                                </Typography>}
                                                            </Grid>
                                                            <Grid item xs={12} md={12}>
                                                                <Typography
                                                                    sx={{
                                                                        fontSize: "16px",
                                                                        fontWeight: "400",
                                                                        color: "text.primary",
                                                                        textAlign: "left",
                                                                        width: "100%",
                                                                        fontFamily: "Poppins",
                                                                    }}
                                                                >
                                                                    Date of Birth
                                                                </Typography>
                                                                {isLoading ? (
                                                                    <Skeleton height={40} />
                                                                ) : (
                                                                    <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start", pt: "4px", flexWrap: "wrap" }}>
                                                                        <TextField
                                                                            size="small"
                                                                            placeholder="YYYY-MM-DD"
                                                                            value={birthDateEdit}
                                                                            onChange={(e) => setBirthDateEdit(e.target.value)}
                                                                            helperText="Used for birthday emails. Also editable from KYC."
                                                                            sx={{ minWidth: 180, maxWidth: 260 }}
                                                                        />
                                                                        <Button
                                                                            size="small"
                                                                            variant="contained"
                                                                            disabled={savingDob}
                                                                            onClick={handleSaveBirthDate}
                                                                            sx={{
                                                                                mt: "2px",
                                                                                textTransform: "none",
                                                                                bgcolor: "#46adf2",
                                                                                "&:hover": { bgcolor: "#2d95db" },
                                                                            }}
                                                                        >
                                                                            {savingDob ? "Saving…" : "Save DOB"}
                                                                        </Button>
                                                                    </Box>
                                                                )}
                                                            </Grid>
                                                            <Grid item xs={12} md={12}>
                                                                <Typography
                                                                    sx={{
                                                                        fontSize: "16px",
                                                                        fontWeight: "400",
                                                                        color: "#000000",
                                                                        textAlign: "left",
                                                                        width: "100%",
                                                                        fontFamily: "Poppins",
                                                                    }}
                                                                >
                                                                    Client Communications
                                                                </Typography>
                                                                {isLoading ? (
                                                                    <Skeleton height={32} />
                                                                ) : (
                                                                    <FormControlLabel
                                                                        sx={{ mt: 0.5, ml: 0 }}
                                                                        control={
                                                                            <Switch
                                                                                checked={customerDetails[0]?.do_not_contact === true}
                                                                                onChange={(e) => handleToggleDoNotContact(e.target.checked)}
                                                                                color="warning"
                                                                            />
                                                                        }
                                                                        label={
                                                                            <Typography sx={{ fontSize: 14, fontFamily: "Poppins" }}>
                                                                                Do not contact (exclude from campaign emails)
                                                                            </Typography>
                                                                        }
                                                                    />
                                                                )}
                                                            </Grid>
                                                            <Grid item xs={12} md={12}>
                                                                <Typography
                                                                    sx={{
                                                                        fontSize: "16px",
                                                                        fontWeight: "400",
                                                                        color: "#000000",
                                                                        textAlign: "left",
                                                                        width: "100%",
                                                                        fontFamily: "Poppins",
                                                                    }}
                                                                >
                                                                    Document
                                                                </Typography>
                                                                {isLoading ? <Skeleton height={50} width={80} /> : (
                                                                    <Box sx={{ paddingTop: "8px" }}>
                                                                        {customerDetails[0]?.document ? (
                                                                            <Tooltip title="Click to view document">
                                                                                <Box
                                                                                    component="span"
                                                                                    sx={{ cursor: "pointer", display: "inline-block" }}
                                                                                    onClick={() => {
                                                                                        const docUrl = customerDetails[0]?.document?.replace(/fiddux-5a1f1\.appspot\.com/g, "fidduxai-rowy.appspot.com") || customerDetails[0]?.document;
                                                                                        window.open(docUrl, "_blank");
                                                                                    }}
                                                                                >
                                                                                    {customerDetails[0]?.mediaType === "image" ? (
                                                                                        <img
                                                                                            src={customerDetails[0]?.document?.replace(/fiddux-5a1f1\.appspot\.com/g, "fidduxai-rowy.appspot.com") || customerDetails[0]?.document}
                                                                                            alt="Uploaded document"
                                                                                            style={{
                                                                                                width: "60px",
                                                                                                height: "60px",
                                                                                                borderRadius: "8px",
                                                                                                objectFit: "cover",
                                                                                            }}
                                                                                        />
                                                                                    ) : (
                                                                                        <img src={DOCUMENT_ICON} alt="document" style={{ width: "50px" }} />
                                                                                    )}
                                                                                </Box>
                                                                            </Tooltip>
                                                                        ) : (
                                                                            <Typography sx={{ fontSize: "14px", color: "text.secondary" }}>N/A</Typography>
                                                                        )}
                                                                    </Box>
                                                                )}
                                                            </Grid>
                                                            <Grid item xs={12} md={12}>
                                                                <Typography
                                                                    sx={{
                                                                        fontSize: "16px",
                                                                        fontWeight: "400",
                                                                        color: "text.primary",
                                                                        textAlign: "left",
                                                                        width: "100%",
                                                                        fontFamily: "Poppins",
                                                                    }}
                                                                >
                                                                    Registered Date
                                                                </Typography>
                                                                {isLoading ? <Skeleton height={20} /> : <Typography
                                                                    sx={{
                                                                        fontSize: "14px",
                                                                        fontWeight: "400",
                                                                        color: "text.primary",
                                                                        textAlign: "left",
                                                                        width: "100%",
                                                                        paddingTop: "4px",
                                                                        fontFamily: "Poppins",
                                                                    }}
                                                                >
                                                                    {customerDetails[0]?.registered_date
                                                                        ? (() => {
                                                                            const registeredDate = customerDetails[0]?.registered_date;
                                                                            let date;
                                                                            if (registeredDate?.toDate) {
                                                                                // Firestore Timestamp object
                                                                                date = moment(registeredDate.toDate());
                                                                            } else if (registeredDate?.seconds) {
                                                                                // Firestore timestamp with seconds/nanoseconds
                                                                                const milliseconds = registeredDate.seconds * 1000 + Math.floor((registeredDate.nanoseconds || 0) / 1000000);
                                                                                date = moment(milliseconds);
                                                                            } else {
                                                                                // Regular date
                                                                                date = moment(registeredDate);
                                                                            }
                                                                            return date.format("MMMM Do YYYY, h:mm:ss A");
                                                                        })()
                                                                        : "N/A"}
                                                                </Typography>}
                                                            </Grid>
                                                            <Grid item xs={12} md={12}>
                                                                <Typography
                                                                    sx={{
                                                                        fontSize: "16px",
                                                                        fontWeight: "400",
                                                                        color: "text.primary",
                                                                        textAlign: "left",
                                                                        width: "100%",
                                                                        fontFamily: "Poppins",
                                                                        mb: 1,
                                                                        display: "flex",
                                                                        alignItems: "center",
                                                                        gap: 1,
                                                                    }}
                                                                >
                                                                    <VerifiedUserIcon fontSize="small" />
                                                                    KYC / Compliance
                                                                </Typography>
                                                                {isLoading ? (
                                                                    <Skeleton height={40} />
                                                                ) : (
                                                                    <Box sx={{ mb: 2 }}>
                                                                        {kycProfile ? (
                                                                            <>
                                                                                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center", mb: 1 }}>
                                                                                    <KycStatusChip status={kycProfile.kyc_status} />
                                                                                    <Chip size="small" label={`${kycProfile.completeness_score ?? 0}% complete`} />
                                                                                    {kycProfile.wallet_recon_summary?.status && (
                                                                                        <Chip
                                                                                            size="small"
                                                                                            variant="outlined"
                                                                                            label={`Wallets: ${kycProfile.wallet_recon_summary.status}`}
                                                                                        />
                                                                                    )}
                                                                                </Box>
                                                                                <Button
                                                                                    size="small"
                                                                                    variant="outlined"
                                                                                    endIcon={<OpenInNewIcon />}
                                                                                    sx={{ textTransform: "none" }}
                                                                                    onClick={() =>
                                                                                        navigate("/admin/kyc-control-center")
                                                                                    }
                                                                                >
                                                                                    Open KYC Control Center
                                                                                </Button>
                                                                            </>
                                                                        ) : (
                                                                            <Typography sx={{ fontSize: 14, color: "text.secondary", fontFamily: "Poppins" }}>
                                                                                No KYC profile yet. Sync from KYC Control Center.
                                                                            </Typography>
                                                                        )}
                                                                    </Box>
                                                                )}
                                                            </Grid>
                                                            <Grid item xs={12} md={12}>
                                                                <Typography
                                                                    sx={{
                                                                        fontSize: "16px",
                                                                        fontWeight: "400",
                                                                        color: "text.primary",
                                                                        textAlign: "left",
                                                                        width: "100%",
                                                                        fontFamily: "Poppins",
                                                                        mb: 1,
                                                                        display: "flex",
                                                                        alignItems: "center",
                                                                        gap: 1,
                                                                    }}
                                                                >
                                                                    <AccountBalanceWalletIcon fontSize="small" />
                                                                    Crypto Wallets
                                                                </Typography>
                                                                {isLoading ? (
                                                                    <Skeleton height={80} />
                                                                ) : (
                                                                    <Box sx={{ mb: 2 }}>
                                                                        {cryptoWallets.length === 0 ? (
                                                                            <Typography sx={{ fontSize: "14px", color: "text.secondary", fontFamily: "Poppins" }}>
                                                                                No active wallets registered.
                                                                            </Typography>
                                                                        ) : (
                                                                            cryptoWallets.map((w) => (
                                                                                <Box
                                                                                    key={w.wallet_doc_id || w.id}
                                                                                    sx={{
                                                                                        display: "flex",
                                                                                        flexWrap: "wrap",
                                                                                        alignItems: "center",
                                                                                        gap: 1,
                                                                                        mb: 1,
                                                                                        fontSize: "13px",
                                                                                        fontFamily: "monospace",
                                                                                    }}
                                                                                >
                                                                                    <span>{formatWalletDisplay(w)}</span>
                                                                                    {w.is_default && (
                                                                                        <Chip size="small" label="Default" color="primary" variant="outlined" />
                                                                                    )}
                                                                                    <Chip size="small" label="Active" color="success" />
                                                                                </Box>
                                                                            ))
                                                                        )}
                                                                        <Button
                                                                            size="small"
                                                                            variant="outlined"
                                                                            endIcon={<OpenInNewIcon />}
                                                                            sx={{ mt: 1, textTransform: "none" }}
                                                                            onClick={() =>
                                                                                navigate(
                                                                                    `/admin/wallet-management?client=${customerDetails[0]?.doc_id}`
                                                                                )
                                                                            }
                                                                        >
                                                                            Manage Wallets
                                                                        </Button>
                                                                    </Box>
                                                                )}
                                                            </Grid>
                                                            <Grid item xs={12} md={12}>
                                                                <Typography
                                                                    sx={{
                                                                        fontSize: "16px",
                                                                        fontWeight: "400",
                                                                        color: "text.primary",
                                                                        textAlign: "left",
                                                                        width: "100%",
                                                                        fontFamily: "Poppins",
                                                                        mb: 1,
                                                                        display: "flex",
                                                                        alignItems: "center",
                                                                        gap: 1,
                                                                    }}
                                                                >
                                                                    <AccountBalanceWalletIcon fontSize="small" />
                                                                    Withdrawal Payout
                                                                </Typography>
                                                                {isLoading ? (
                                                                    <Skeleton height={40} />
                                                                ) : (
                                                                    <Box sx={{ mb: 2 }}>
                                                                        {customerDetails[0]?.payout_mode === "fiat_manual" && (
                                                                            <Chip
                                                                                size="small"
                                                                                color="warning"
                                                                                label="Paid in FIAT — manual"
                                                                                sx={{ mb: 1 }}
                                                                            />
                                                                        )}
                                                                        <FormControl fullWidth size="small">
                                                                            <Select
                                                                                value={customerDetails[0]?.payout_mode === "fiat_manual" ? "fiat_manual" : "crypto"}
                                                                                onChange={(e) => handleUpdatePayoutMode(customerDetails[0]?.doc_id, e.target.value)}
                                                                            >
                                                                                <MenuItem value="crypto">Crypto wallet (default)</MenuItem>
                                                                                <MenuItem value="fiat_manual">Manual FIAT (paid in Brazil)</MenuItem>
                                                                            </Select>
                                                                        </FormControl>
                                                                        <Typography sx={{ fontSize: "12px", color: "text.secondary", fontFamily: "Poppins", mt: 0.5 }}>
                                                                            Manual FIAT: Auto-Pay never processes this client; they can withdraw without a crypto wallet.
                                                                        </Typography>
                                                                    </Box>
                                                                )}
                                                            </Grid>
                                                            <Grid item xs={12} md={12}>
                                                                <Typography
                                                                    sx={{
                                                                        fontSize: "16px",
                                                                        fontWeight: "400",
                                                                        color: "text.primary",
                                                                        textAlign: "left",
                                                                        width: "100%",
                                                                        fontFamily: "Poppins",
                                                                        mb: 1
                                                                    }}
                                                                >
                                                                    Actions
                                                                </Typography>
                                                                {isLoading ? <Skeleton height={200} /> : <Box sx={{
                                                                    display: 'flex',
                                                                    flexDirection: 'column',
                                                                    gap: '12px'
                                                                }}>
                                                                    {/* Edit Customer Button (movido da lista Customer Management) */}
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                                        <Tooltip title='Edit Customer' key="edit">
                                                                            <IconButton
                                                                                sx={{
                                                                                    width: "30px",
                                                                                    height: "30px",
                                                                                    borderRadius: "4px",
                                                                                    background: "green",
                                                                                    "&:hover": { background: "linear-gradient(148.97deg, #0C2C50 0%, #450F5B 100%)" }
                                                                                }}
                                                                                onClick={() => navigate("/admin/editcustomer", {
                                                                                    state: { customData: sanitizeForNavigation(customerDetails[0]) },
                                                                                })}
                                                                            >
                                                                                <EditIcon sx={{ color: "#fff", fontSize: "18px" }} />
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                        <Typography sx={{ fontSize: "14px", fontFamily: "Poppins", color: "text.primary" }}>
                                                                            Edit Customer
                                                                        </Typography>
                                                                    </Box>

                                                                    {/* Block/UnBlock Button */}
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                                        {customerDetails[0]?.status === 1 ? 
                                                                            <>
                                                                                <Tooltip title='Block' key="block">
                                                                                    <IconButton
                                                                                        sx={{
                                                                                            width: "30px",
                                                                                            height: "30px",
                                                                                            borderRadius: "4px", 
                                                                                            background: "red", 
                                                                                            "&:hover": { background: "#C62828" }
                                                                                        }}
                                                                                        onClick={() => handleUpdateBlockUnBlockStatus(customerDetails[0]?.doc_id, 2)} 
                                                                                    >
                                                                                        <PersonIcon sx={{ color: "#fff" }} />
                                                                                    </IconButton>
                                                                                </Tooltip>
                                                                                <Typography sx={{ fontSize: "14px", fontFamily: "Poppins", color: "text.primary" }}>
                                                                                    Block User
                                                                                </Typography>
                                                                            </>
                                                                            : 
                                                                            <>
                                                                                <Tooltip title='UnBlock' key="UnBlock">
                                                                                    <IconButton
                                                                                        sx={{
                                                                                            width: "30px",
                                                                                            height: "30px",
                                                                                            borderRadius: "4px", 
                                                                                            background: "red", 
                                                                                            "&:hover": { background: "#c62828" }
                                                                                        }}
                                                                                        onClick={() => handleUpdateBlockUnBlockStatus(customerDetails[0]?.doc_id, 1)}
                                                                                    >
                                                                                        <PersonOffIcon sx={{ color: "#fff" }} />
                                                                                    </IconButton>
                                                                                </Tooltip>
                                                                                <Typography sx={{ fontSize: "14px", fontFamily: "Poppins", color: "text.primary" }}>
                                                                                    Unblock User
                                                                                </Typography>
                                                                            </>
                                                                        }
                                                                    </Box>
                                                                    
                                                                    {/* Delete Button */}
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                                        <Tooltip title='Delete' key="delete">
                                                                            <IconButton
                                                                                sx={{
                                                                                    width: "30px",
                                                                                    height: "30px",
                                                                                    borderRadius: "4px", 
                                                                                    background: "#d32f2f", 
                                                                                    "&:hover": { background: "#c62828" }
                                                                                }}
                                                                                onClick={() => handleDeleteUser(
                                                                                    customerDetails[0]?.doc_id, 
                                                                                    customerDetails[0]?.email, 
                                                                                    customerDetails[0]?.full_name
                                                                                )}
                                                                            >
                                                                                <DeleteIcon sx={{ color: "#fff", fontSize: "18px" }} />
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                        <Typography sx={{ fontSize: "14px", fontFamily: "Poppins", color: "text.primary" }}>
                                                                            Delete User
                                                                        </Typography>
                                                                    </Box>

                                                                    {/* Resend Verification Email Button */}
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                                        <Tooltip title='Resend Verification Email' key="resend-verification">
                                                                            <IconButton
                                                                                sx={{
                                                                                    width: "30px",
                                                                                    height: "30px",
                                                                                    borderRadius: "4px", 
                                                                                    background: "#2196f3", 
                                                                                    "&:hover": { background: "#1976d2" }
                                                                                }}
                                                                                onClick={() => handleResendVerificationEmail(
                                                                                    customerDetails[0]?.doc_id, 
                                                                                    customerDetails[0]?.email
                                                                                )}
                                                                            >
                                                                                <EmailIcon sx={{ color: "#fff", fontSize: "18px" }} />
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                        <Typography sx={{ fontSize: "14px", fontFamily: "Poppins", color: "text.primary" }}>
                                                                            Resend Verification Email
                                                                        </Typography>
                                                                    </Box>

                                                                    {/* Send Password Reset Email Button */}
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                                        <Tooltip title='Send Password Reset Email' key="send-password-reset">
                                                                            <IconButton
                                                                                sx={{
                                                                                    width: "30px",
                                                                                    height: "30px",
                                                                                    borderRadius: "4px", 
                                                                                    background: "#9c27b0", 
                                                                                    "&:hover": { background: "#7b1fa2" }
                                                                                }}
                                                                                onClick={() => handleSendPasswordResetEmail(
                                                                                    customerDetails[0]?.doc_id, 
                                                                                    customerDetails[0]?.email
                                                                                )}
                                                                            >
                                                                                <LockResetIcon sx={{ color: "#fff", fontSize: "18px" }} />
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                        <Typography sx={{ fontSize: "14px", fontFamily: "Poppins", color: "text.primary" }}>
                                                                            Send Password Reset Email
                                                                        </Typography>
                                                                    </Box>

                                                                    {/* Reset Yield Toggle Count (movido da lista Customer Management) */}
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                                        <Tooltip title='Reset Yield Toggle Count' key="reset-toggle">
                                                                            <IconButton
                                                                                sx={{
                                                                                    width: "30px",
                                                                                    height: "30px",
                                                                                    borderRadius: "4px",
                                                                                    background: "#ff9800",
                                                                                    "&:hover": { background: "#f57c00" }
                                                                                }}
                                                                                onClick={() => handleResetYieldToggleCount(
                                                                                    customerDetails[0]?.doc_id,
                                                                                    customerDetails[0]?.email,
                                                                                    customerDetails[0]?.full_name
                                                                                )}
                                                                            >
                                                                                <RefreshIcon sx={{ color: "#fff", fontSize: "18px" }} />
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                        <Typography sx={{ fontSize: "14px", fontFamily: "Poppins", color: "text.primary" }}>
                                                                            Reset Yield Toggle Count
                                                                        </Typography>
                                                                    </Box>
                                                                </Box>}
                                                            </Grid>
                                                        </Grid>
                                                    </CardContent>
                                                </Card>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>
                    {isMobile && (
                        <Grid item xs={12}>
                            <AccountOperationsFreeze
                                customer={customerDetails[0]}
                                isLoading={isLoading}
                                onChanged={getCustomerDetails}
                            />
                        </Grid>
                    )}
                    <Grid item xs={12} sm={7} md={7} lg={9}>
                        <Grid container spacing={3}>

                            <Grid item xs={12} md={4}>
                                <Typography
                                    sx={{
                                        fontSize: "16px",
                                        fontWeight: "400",
                                        color: "text.primary",
                                        textAlign: "left",
                                        width: "100%",
                                        fontFamily: "Poppins",
                                    }}
                                >
                                    Current Balance
                                </Typography>
                                {isLoading ? <Skeleton height={20} /> : <Typography
                                    sx={{
                                        fontSize: "14px",
                                        fontWeight: "400",
                                        color: "text.primary",
                                        textAlign: "left",
                                        width: "100%",
                                        paddingTop: "4px",
                                        textTransform: "capitalize",
                                        fontFamily: "Poppins",
                                    }}
                                >
                                    {new Intl.NumberFormat('en-US', {
                                        style: 'currency',
                                        currency: 'USD',
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                    }).format(customerDetails[0]?.net_balance)}
                                </Typography>}
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Typography
                                    sx={{
                                        fontSize: "16px",
                                        fontWeight: "400",
                                        color: "text.primary",
                                        textAlign: "left",
                                        width: "100%",
                                        fontFamily: "Poppins",
                                    }}
                                >
                                    Total Deposits
                                </Typography>
                                {isLoading ? <Skeleton height={20} /> : <Typography
                                    sx={{
                                        fontSize: "14px",
                                        fontWeight: "400",
                                        color: "text.primary",
                                        textAlign: "left",
                                        width: "100%",
                                        paddingTop: "4px",
                                        fontFamily: "Poppins",
                                    }}
                                >
                                    {new Intl.NumberFormat('en-US', {
                                        style: 'currency',
                                        currency: 'USD',
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                    }).format(totalDeposit)}
                                </Typography>}
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Typography
                                    sx={{
                                        fontSize: "16px",
                                        fontWeight: "400",
                                        color: "text.primary",
                                        textAlign: "left",
                                        width: "100%",
                                        fontFamily: "Poppins",
                                    }}
                                >
                                    Total Withdrawals
                                </Typography>
                                {isLoading ? <Skeleton height={20} /> : <Typography
                                    sx={{
                                        fontSize: "14px",
                                        fontWeight: "400",
                                        color: "text.primary",
                                        textAlign: "left",
                                        width: "100%",
                                        paddingTop: "4px",
                                        textTransform: "capitalize",
                                        fontFamily: "Poppins",
                                    }}
                                >
                                    {new Intl.NumberFormat('en-US', {
                                        style: 'currency',
                                        currency: 'USD',
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                    }).format(totalWithdraw)}
                                </Typography>}
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Typography
                                    sx={{
                                        fontSize: "16px",
                                        fontWeight: "400",
                                        color: "text.primary",
                                        textAlign: "left",
                                        width: "100%",
                                        fontFamily: "Poppins",
                                    }}
                                >
                                    Total Performance
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: "14px",
                                        fontWeight: "400",
                                        color: totalPerformances > 0 ? "green" : "red",
                                        textAlign: "left",
                                        width: "100%",
                                        paddingTop: "4px",
                                        textTransform: "capitalize",
                                        fontFamily: "Poppins",
                                    }}
                                >
                                    {totalPerformances ? new Intl.NumberFormat('en-US', {
                                        style: 'currency',
                                        currency: 'USD',
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                    }).format(totalPerformances) : "--"}
                                </Typography>
                                
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Typography
                                    sx={{
                                        fontSize: "16px",
                                        fontWeight: "400",
                                        color: "text.primary",
                                        textAlign: "left",
                                        width: "100%",
                                        fontFamily: "Poppins",
                                    }}
                                >
                                    Last Month's Performance
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: "16px",
                                        fontWeight: "400",
                                        color: "text.primary",
                                        textAlign: "left",
                                        width: "100%",
                                        fontFamily: "Poppins",
                                    }}
                                >
                                    {lastMonthPerformances[0]?.profit_loss_percent ? `${lastMonthPerformances[0]?.profit_loss_percent.toFixed(2)} %` : `0 %`}
                                </Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Box sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: 'flex-end',
                                    gridGap: '10px'
                                }}>
                                    
                                    <Button
                                        sx={{
                                            background: "linear-gradient(148.97deg, #0C2C50 0%, #450F5B 100%)",
                                            color: "#fff",
                                            height: '46px',
                                            padding: "0px 20px",
                                            textTransform: 'capitalize',
                                            "&:hover": { background: "linear-gradient(148.97deg, #0C2C50 0%, #450F5B 100%)", color: "#fff" },
                                        }}
                                        onClick={() => setOpenDepositeModel(true)}
                                    >
                                        + Add Deposit
                                    </Button>
                                    <Button
                                        sx={{
                                            background: "linear-gradient(148.97deg, #0C2C50 0%, #450F5B 100%)",
                                            color: "#fff",
                                            height: '46px',
                                            padding: "0px 20px",
                                            textTransform: 'capitalize',
                                            "&:hover": { background: "linear-gradient(148.97deg, #0C2C50 0%, #450F5B 100%)", color: "#fff" },
                                        }}
                                        onClick={() => {
                                            if (customerDetails[0]?.net_balance === 0) {
                                                toast.error("You don't have sufficient balance.")
                                            } else {
                                                setOpenWithdrowModel(true)
                                            }
                                        }
                                        }
                                    >
                                        + Add Withdraw
                                    </Button>
                                    <Button
                                        sx={{
                                            background: "#fff",
                                            color: "#92400e",
                                            border: "1px solid #f59e0b",
                                            height: '46px',
                                            padding: "0px 16px",
                                            textTransform: 'capitalize',
                                            fontFamily: "Poppins",
                                            "&:hover": { background: "#fffbeb", borderColor: "#d97706" },
                                        }}
                                        startIcon={<SwapHorizIcon />}
                                        onClick={() => setOpenCapitalRouting(true)}
                                    >
                                        Capital Routing
                                    </Button>
                                </Box>
                            </Grid>
                            <Grid item xs={12}>
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: 'space-between',
                                    }}
                                >
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            fontSize: "20px",
                                            color: "text.primary",
                                            fontWeight: "500",
                                            fontFamily: "Poppins",
                                        }}
                                    >
                                        Payment Information
                                    </Typography>

                                    <Tooltip title="Filter Payment History">
                                        <IconButton
                                            onClick={handleClick}
                                            size="small"
                                            sx={{ ml: 2 }}
                                            aria-controls={open ? "account-menu" : undefined}
                                            aria-haspopup="true"
                                            aria-expanded={open ? "true" : undefined}
                                        >
                                            <FilterAltIcon
                                                sx={{ width: 40, height: 40, color: "text.primary" }}
                                            ></FilterAltIcon>
                                        </IconButton>
                                    </Tooltip>
                                    <Menu
                                        anchorEl={anchorEl}
                                        id="account-menu"
                                        open={open}
                                        onClose={handleClose}
                                        onClick={handleClose}
                                        PaperProps={{
                                            elevation: 0,
                                            sx: {
                                                overflow: "visible",
                                                filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
                                                mt: 1.5,
                                                "& .MuiAvatar-root": {
                                                    width: 32,
                                                    height: 32,
                                                    ml: -0.5,
                                                    mr: 1,
                                                },
                                                "&::before": {
                                                    content: '""',
                                                    display: "block",
                                                    position: "absolute",
                                                    top: 0,
                                                    right: 14,
                                                    width: 10,
                                                    height: 10,
                                                    bgcolor: "background.paper",
                                                    transform: "translateY(-50%) rotate(45deg)",
                                                    zIndex: 0,
                                                },
                                            },
                                        }}
                                        transformOrigin={{ horizontal: "right", vertical: "top" }}
                                        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                                    >
                                        <MenuItem
                                            sx={{
                                                fontSize: "18px",
                                                fontWeight: "600",
                                                fontFamily: "Poppins",
                                            }}
                                            onClick={() => handleUpdateStatusDepositWithdraw(null)}
                                        >
                                            All Transaction
                                        </MenuItem>
                                        <MenuItem
                                            sx={{
                                                fontSize: "18px",
                                                fontWeight: "600",
                                                fontFamily: "Poppins",
                                            }}
                                            onClick={() => handleUpdateStatusDepositWithdraw(0)}
                                        >
                                            Deposit
                                        </MenuItem>
                                        <MenuItem

                                            sx={{
                                                fontSize: "18px",
                                                fontWeight: "600",
                                                fontFamily: "Poppins",
                                            }}
                                            onClick={() => handleUpdateStatusDepositWithdraw(1)}
                                        >
                                            Withdraw
                                        </MenuItem>
                                    </Menu>
                                </Box>
                                <Box sx={{ width: "100%", mt: 1 }}>
                                    <TableContainer
                                        rows={transactionHistory}
                                        columns={columns}
                                        loading={isBusy}
                                        page={page}
                                        pageSize={pageSize}
                                        totalCount={totalCount}
                                        handlePageChange={handlePageChange}
                                        onPageSizeChange={handlePageSizeChange}
                                        paginationShow={true}
                                        sx={{
                                            height: "calc(100vh - 280px)",
                                            minHeight: 420,
                                        }}
                                    />
                                </Box>
                            </Grid>
                            {!isMobile && (
                                <Grid item xs={12}>
                                    <AccountOperationsFreeze
                                        customer={customerDetails[0]}
                                        isLoading={isLoading}
                                        onChanged={getCustomerDetails}
                                    />
                                </Grid>
                            )}
                        </Grid>
                    </Grid>
                </Grid>
            </Box>
            <NewDepositModel
                open={openDepositeModel}
                onClose={() => setOpenDepositeModel(false)}
                handleOnClose={() => setOpenDepositeModel(false)}
                customerDeposit={customerDetails}
                handleSubmited={handleSubmitedDeposit}
            />
            <NewWithdrowModel
                open={openWithdrowModel}
                onClose={() => setOpenWithdrowModel(false)}
                handleOnClose={() => setOpenWithdrowModel(false)}
                customerWithdraw={customerDetails}
                handleSubmited={handleSubmitedWithdraw}
            />
            <CapitalRoutingWizard
                open={openCapitalRouting}
                onClose={() => setOpenCapitalRouting(false)}
                sender={customerDetails[0]}
                onCompleted={() => getCustomerDetails(customerDetails[0]?.doc_id)}
            />
        </Fragment>
    );
};

export default ViewCustomer;
